'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { cn } from '@/lib/utils'

interface ThemeToggleProps {
  className?: string
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const isDark = mounted ? resolvedTheme === 'dark' : true // Default to dark for SSR consistency
  const label = mounted ? `Switch to ${isDark ? 'light' : 'dark'} mode` : 'Toggle theme'

  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {mounted ? (
        <>
          <Sun
            className={cn(
              'absolute h-4 w-4 rotate-0 scale-100 transition-all',
              !isDark && 'rotate-0 scale-100',
              isDark && 'rotate-90 scale-0'
            )}
          />
          <Moon
            className={cn(
              'absolute h-4 w-4 rotate-90 scale-0 transition-all',
              isDark && 'rotate-0 scale-100',
              !isDark && '-rotate-90 scale-0'
            )}
          />
        </>
      ) : (
        <Sun className="h-4 w-4 animate-pulse" />
      )}
      <span className="sr-only">{label}</span>
    </button>
  )
}
